import { createFileRoute } from '@tanstack/react-router';
import { useReducer, useState } from 'react';
import { HStack, VStack } from '@/components/Layout';
import { AddStepperCompleteDialog } from '@/components/Stepper';
import type { WorkerType } from '@/constants/workerType';
import Processing from '@/features/import-csv/-components/Processing';
import type { Membership } from '@/features/people-group/-api';
import { type OrganizationClauseData, OrganizationClauseEdit, RuleCard, RuleClause, SetIndividually, SetMembership } from '@/features/rule-and-policy';
import { SETTING_TYPE } from '@/features/worker-type-rule/types';
import { useSelectedWorkerFilter } from '@/hooks/useSelectedWorkerFilter';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { LIST_EMPTY_DATA } from '@/lib/listPage';
import AddStepperNavigation from '../-components/AddStepperNavigation';
import AddStepperPanelProgress from '../-components/AddStepperPanelProgress';
import AddStepperStepTitleCard from '../-components/AddStepperStepTitleCard';
import BasicForm from '../-components/BasicForm';
import ConfirmPeopleGroup from '../-components/ConfirmPeopleGroup';
import { PEOPLE_GROUP_CREATE_HASH } from '../-constants';
import useBasicForm from '../-hooks/useBasicForm';
import useHash from '../-hooks/useHash';
import useCreatePeopleGroup from '../-mutations/useCreatePeopleGroup';
import usePreviewPeopleGroup from '../-queries/usePreviewPeopleGroup';
import individualsConditionReducer from '../-reducers/individualsConditionReducer';
import type { SelectedMembershipState } from '../-types';
import useGetPeopleGroups from '../../../_list/people-groups/-queries/useGetPeopleGroups';
export const Route = createFileRoute('/_staff/settings/permission-and-roles/_form/people-groups/new/')({
  component: RouteComponent
});
const BASE_LINK = '/settings/permission-and-roles/people-groups/new';
function RouteComponent() {
  const {
    hash
  } = useHash();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.settings.permissionAndRoles.createPeopleGroup'
  });
  const {
    data: peopleGroupsRes
  } = useGetPeopleGroups({
    search: {
      category: 'ALL',
      pageIndex: 0,
      pageSize: 100
    }
  });
  const {
    basicFormState,
    handleChange,
    isDuplicateName
  } = useBasicForm({
    data: undefined,
    existingPeopleGroups: peopleGroupsRes ?? LIST_EMPTY_DATA
  });

  /**
   * NOTE: zustandまたはxstateに移行すべき
   * useState, useReducerを使っていることで、propsのバケツリレーが発生する
   * また、Membership、CustomFilter、IndividualsConditionのstateがそれぞれ独立しているため、
   * ここもまとめるべきかも合わせて検討が必要
   */
  const {
    selectedMemberships,
    changeWorkerFilterSettingType,
    changeWorkerFilterCustomFilter
  } = useSelectedWorkerFilter<Membership, SelectedMembershipState>({
    workerFilters: [],
    createStateWrapper: (membership: Membership) => ({
      membership
    }),
    extractWorkerFilter: (state: SelectedMembershipState) => state.membership,
    createDefaultWorkerFilter: (workerType: WorkerType) => ({
      id: '',
      workerType,
      settingType: SETTING_TYPE.NO_ONE,
      customFilter: null
    }),
    includeEditingState: false
  });
  const [individualsConditionState, individualsConditionStateHandler] = useReducer(individualsConditionReducer, {
    includeWorkers: [],
    excludeWorkers: []
  });
  const [organizationTreeState, setOrganizationTreeState] = useState<OrganizationClauseData>({
    nodes: [],
    selectedDepartments: [],
    selectedWorkerTypes: []
  });
  const {
    preview: mutatePreviewPeopleGroup,
    isPending: isPreviewPeopleGroupPending,
    previewPeopleGroupResponse,
    memberList: previewMemberList
  } = usePreviewPeopleGroup();
  const {
    mutate: mutateCreatePeopleGroup,
    isPending: isCreatePeopleGroupPending,
    isComplete
  } = useCreatePeopleGroup();
  const isPending = isPreviewPeopleGroupPending || isCreatePeopleGroupPending;
  return <HStack className="gap-9 p-10 h-[calc(100vh-64px)] w-full">
      <AddStepperNavigation baseLink={BASE_LINK} currentHash={hash} isEdit={false} />

      <VStack className="gap-6 h-full min-w-0 w-full">
        {isPending && <Processing hash={hash} />}

        {!isPending && <AddStepperStepTitleCard hash={hash} />}

        {!isPending && hash === PEOPLE_GROUP_CREATE_HASH.FORM && <BasicForm basicFormState={basicFormState} handleChange={handleChange} isDuplicateName={isDuplicateName} />}

        {!isPending && hash === PEOPLE_GROUP_CREATE_HASH.SET_MEMBERSHIP && <VStack className="overflow-y-auto">
            <RuleCard ruleId={'organization-tree'}>
              <RuleClause clause={{
            id: 'workerType',
            type: 'workerType'
          }} viewContent={<SetMembership selectedMemberships={selectedMemberships} changeWorkerFilterSettingType={changeWorkerFilterSettingType} changeWorkerFilterCustomFilter={changeWorkerFilterCustomFilter} />} />
              <RuleClause clause={{
            id: 'organization',
            type: 'organization'
          }} viewContent={<OrganizationClauseEdit data={organizationTreeState} onChange={setOrganizationTreeState} />} />
              <RuleClause clause={{
            id: 'individually',
            type: 'individual'
          }} viewContent={<SetIndividually state={individualsConditionState} stateHandler={individualsConditionStateHandler} isEditing={true} />} />
            </RuleCard>
          </VStack>}

        {!isPending && hash === PEOPLE_GROUP_CREATE_HASH.CONFIRM_PEOPLE_GROUP && <ConfirmPeopleGroup previewPeopleGroupResponse={previewPeopleGroupResponse} memberList={previewMemberList} />}

        {!isPending && <AddStepperPanelProgress baseLink={BASE_LINK} currentHash={hash} basicFormState={basicFormState} selectedMemberships={selectedMemberships} organizationTreeState={organizationTreeState} individualsConditionState={individualsConditionState} mutatePreviewPeopleGroup={mutatePreviewPeopleGroup} mutateCreateOrUpdatePeopleGroup={mutateCreatePeopleGroup} isEdit={false} isDuplicateName={isDuplicateName} />}

        {isComplete && <AddStepperCompleteDialog completeMessage={t('addStepperCompleteDialog.create.completeMessage')} completeLinkButtonText={t('addStepperCompleteDialog.create.completeLinkButtonText')} completeLinkUrl="/settings/permission-and-roles/people-groups" />}
      </VStack>
    </HStack>;
}